import React, { useContext } from "react";
import * as yup from "yup";
import PropTypes from "prop-types";
import { useFormik } from "formik";

import { TextField } from "components/TextField";
import { useFetchPost } from "hooks/fetch/useFetchPost";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";

import { styled } from "@mui/material/styles";
import { CaseManagementParamsContext } from "contexts/CaseManagementParamsContext";
import { Dialog } from "components/Dialog";

export function CreateTableViewDialog({ preferences, open, setOpen, refetch }) {
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { tableState, onSetSavedTableView } = useContext(
    CaseManagementParamsContext
  );

  const { loading, postCallback: createPreference } = useFetchPost(
    "case_management_preferences",
    newPreference => {
      onSetSavedTableView(newPreference);
      onClose();
      addSnackbarToQueue({
        message: "Table view created.",
        severity: "success"
      });
      refetch();
    },
    () => {
      addSnackbarToQueue({
        message: "Table view failed to save.",
        severity: "error"
      });
    }
  );

  const formik = useFormik({
    initialValues: {
      name: ""
    },
    validationSchema: yup.object({
      name: yup
        .string()
        .label("Table view name")
        .max(255)
        .min(3)
        .required()
        .test(
          "is-not-blank",
          "Blank values are not allowed.",
          value => !/^ *$/.test(value)
        )
        .test(
          "is-unique",
          context =>
            `A table view with name "${context.originalValue}" already exists.`,
          value =>
            value &&
            value.trim() &&
            !preferences.map(({ name }) => name).includes(value.trim())
        )
    }),
    onSubmit: values => {
      createPreference({
        name: values.name,
        params: {
          filters: tableState.filters,
          sortBy: tableState.sortBy,
          hiddenColumns: tableState.hiddenColumns,
          columnOrder: tableState.columnOrder,
          groupBy: tableState.groupBy
        }
      });
    }
  });

  const onClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      dialogId="dialog-save-table-view"
      actionsDisabled={loading}
      title="Save New Table View"
      confirmText="Save"
      onSubmit={formik.handleSubmit}
      onClose={onClose}
    >
      <ContentWrapper>
        <NewViewSubtitle>
          Create a new table view with your current preferences, including:
          sorting, filtering, and custom columns.
        </NewViewSubtitle>
        <TextField
          required
          value={formik.values.name}
          name="name"
          id="new-table-view"
          label="Table view name"
          fullWidth
          onBlur={formik.handleBlur}
          helperText={formik.touched.name && formik.errors.name}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
          data-qa="CM-Input-Save-Table-View"
        />
      </ContentWrapper>
    </Dialog>
    // <form id="new-saved-view-form" onSubmit={formik.handleSubmit} noValidate>
    //   <Dialog maxWidth="sm" open={open}>
    //     <DialogTitle>
    //       <NewViewHeader>Save New Table View</NewViewHeader>
    //       <NewViewSubtitle>
    //         Create a new table view with your current preferences, including:
    //         sorting, filtering, and custom columns.
    //       </NewViewSubtitle>
    //     </DialogTitle>
    //     <DialogContent dividers>
    //       <TextField
    //         required
    //         value={formik.values.name}
    //         name="name"
    //         id="new-table-view"
    //         label="Table view name"
    //         fullWidth
    //         onBlur={formik.handleBlur}
    //         helperText={formik.touched.name && formik.errors.name}
    //         onChange={formik.handleChange}
    //         error={formik.touched.name && Boolean(formik.errors.name)}
    //         data-qa="CM-Input-Save-Table-View"
    //       />
    //     </DialogContent>
    //     <Fade in={loading}>
    //       <SavingText>Saving...</SavingText>
    //     </Fade>
    //     <NewViewDialogActions>
    //       <Button
    //         autoFocus
    //         variant="outlined"
    //         color="primary"
    //         onClick={() => onClose()}
    //       >
    //         Cancel
    //       </Button>
    //       <Button
    //         type="submit"
    //         form="new-saved-view-form"
    //         variant="contained"
    //         color="primary"
    //         disabled={loading}
    //       >
    //         Save
    //       </Button>
    //     </NewViewDialogActions>
    //   </Dialog>
    // </form>
  );
}

CreateTableViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  preferences: PropTypes.array.isRequired,
  refetch: PropTypes.func.isRequired
};

const ContentWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(1)};
`;

const NewViewSubtitle = styled("span")`
  font-size: 12px;
`;
